import React from 'react';
import { Arr } from '../../CodesForFoundation';
import { Component, ObjectManager } from '../../concepts/objects';
import { Point } from '../../platform/geom/Point';
import { MouseButtons, MouseInput } from '../../platform/input/MouseInput';
import { Fill } from '../../ui/Fill';
import { ContainerScope, resolve } from '../Container';
import { Listener } from './../../ui';
import { Camera } from './Camera';
import { EditorSession } from './EditorSession';
import { PointerInfo } from './PointerInfo';
import { RootOverride } from './RootOverride';
import { Physical } from './entities/Physical';
import { Physicals } from './entities/Physicals';
import { Burdens } from './extensibility/Burdens';
import { OverlayPointerTarget } from './overlay/Overlay';
import { OverlayComposition } from './overlay/OverlayComposition';
import { SpatialIndex } from './utilities/SpatialIndex';

export class PointerHandler extends React.Component {

    public static contextType = ContainerScope;

    @resolve(Camera) private camera!: Camera;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(ObjectManager) private readonly objects!: ObjectManager;
    @resolve(OverlayComposition) private overlays!: OverlayComposition;
    @resolve(SpatialIndex) private spatialIndex!: SpatialIndex;

    private elmt = React.createRef<HTMLDivElement>();
    private pressed = {} as Record<MouseButtons, boolean>;
    private trash = new Burdens();

    public render() {
        return <Fill id="targeting-layer" ref={this.elmt}>
            <Listener target={window} event="mousedown" callback={this.onMouseDown} />
            <Listener target={window} event="mousemove" callback={this.onMouseAny} />
            <Listener target={window} event="mouseup" callback={this.onMouseUp} />
        </Fill>;
    }

    public componentDidMount = () => this.trash.add(this.objects.on('updated', this.onModelUpdate));
    public componentWillUnmount = () => this.trash.destroy();

    private onMouseDown = (e: MouseEvent) => {
        if (this.isToolLayerTarget(e.target)) {
            this.pressed[e.button] = true;
            this.onMouseAny(e);
        }
    };

    private onMouseUp = (e: MouseEvent) => {
        this.pressed[e.button] = false;
        this.onMouseAny(e);
    };

    private onMouseAny = (e: MouseEvent) => {
        const position = MouseInput.computePosition(e, this.elmt.current);
        const targets = this.resolveTargets(position, e.target);
        // The pressed element is the top target when primary mouse is pressed
        const pressed = this.pressed[MouseButtons.Main] ? Arr.first(targets) : null;
        // Dragging is true if primary button is pressed and we have moved the mouse.
        const dragging = e.type === 'mousemove' && !!this.pressed[MouseButtons.Main];
        // Edging is true if dragging and within the "edge" area of the view
        const edging = dragging && this.isPositionNearEdge(position);
        // If the targets are unchanged, just patch the mouse position and don't send a new target list.  This enables consumers to detect
        // when targets have really changed via a simple equality.
        if (this.areTargetsChanged(targets)) {
            this.session.update(PointerInfo, { position, targets, dragging, pressed, edging });
        }
        else {
            this.session.patch(PointerInfo, { position, dragging, pressed, edging });
        }
    };

    private onModelUpdate = (cs: Component[]) => {
        // If any components deleted, update the targets because they were probably a target if they were deleted by this user.
        if (cs.find(x => !x.alive)) {
            const info = this.session.get(PointerInfo);
            const targets = new Array<any>().concat(
                this.findOverlayTargets(info.position),
                this.findComponentTargets(info.position),
            );
            if (this.areTargetsChanged(targets)) {
                this.session.patch(PointerInfo, { targets });
            }
        }
    };

    private resolveTargets(vpt: Point, eventTarget?: EventTarget) {
        // Was event a tool layer event?
        if (this.isToolLayerTarget(eventTarget)) {
            return new Array<any>().concat(
                this.findOverlayTargets(vpt),
                this.findComponentTargets(vpt),
            );
        }
        return [];
    }

    private isPositionNearEdge(position: Point) {
        const area = this.camera.getViewFrame().inflate(-50);
        return !area.contains(position);
    }

    private isToolLayerTarget(target: any) {
        while (target) {
            if (target && target.id === 'tool-layer') return true;
            target = target.parentElement;
        }
        return false;
    }

    private findOverlayTargets(vpt: Point): OverlayPointerTarget[] {
        const hits = [];
        for (const o of this.overlays) {
            hits.push(...o.test(vpt));
        }
        return hits;
    }

    private findComponentTargets(vpt: Point): Physical[] {
        const mpt = this.camera.toModelPoint(vpt);
        const hits = this.spatialIndex.lookup(mpt);
        const root = this.session.get(RootOverride);
        // Remove hits that are not actually hits
        for (const x of hits) {
            if (x === root || !x.test(mpt)) {
                hits.delete(x);
            }
        }
        // Remove hits where their nearest boundary is a candidate (this has to be done last)
        for (const x of hits) {
            if (hits.has(this.getNearestBoundaryAncestor(x))) {
                hits.delete(x);
            }
        }
        const sorted = Physicals.topToBottom(hits);
        // Reverse because deepest should be first
        return sorted.reverse();
    }

    private areTargetsChanged(next: any[]) {
        const { targets: prev } = this.session.get(PointerInfo) ?? {};
        if (!prev) return true;
        if (next.length !== prev.length) return true;
        for (let i = 0; i < next.length; i++) {
            if (next[i] !== prev[i]) return true;
        }
        return false;
    }

    private getNearestBoundaryAncestor(phys: Physical) {
        phys = phys.parentPhysical;
        if (!phys) return null;
        if (!phys.boundary) return this.getNearestBoundaryAncestor(phys);
        return phys;
    }
}
